// Cierge Labs v1 — Bounded, deterministic content map.
// Selections deliberately drive a templated output. No open-ended generation.

export const USER_TYPES = [
  {
    id: "buyer_sponsor",
    label: "Buyer or sponsor",
    summary:
      "You hold budget or executive authority and are evaluating governed coordination as a strategic capability.",
  },
  {
    id: "operator_domain_lead",
    label: "Operator or domain lead",
    summary:
      "You run an operational domain and need accountable handoffs between people, systems, and AI.",
  },
  {
    id: "partner_integrator",
    label: "Partner or integrator",
    summary:
      "You build, deliver, or integrate workflows for clients and need a governance layer that travels across services.",
  },
  {
    id: "governance_assurance_lead",
    label: "Governance or assurance lead",
    summary:
      "You are responsible for control, audit, or assurance, and need defensible evidence of what was decided and by whom.",
  },
];

export const SECTORS = [
  {
    id: "health",
    label: "Health",
    blurb:
      "Care pathways, clinical decisions, and inter-organisation referrals where accountable handoff matters.",
  },
  {
    id: "supply_chain",
    label: "Supply Chain",
    blurb:
      "Multi-party flows of goods, exceptions, and approvals across operators, carriers, and assurance bodies.",
  },
  {
    id: "mobility",
    label: "Mobility",
    blurb:
      "Coordination across operators, infrastructure, and traveller-facing services with shared accountability.",
  },
  {
    id: "cities_resilience",
    label: "Cities & Resilience",
    blurb:
      "Cross-agency coordination for incidents, services, and continuity, where authority must be attributable.",
  },
];

export const PROBLEMS = [
  {
    id: "requests_approvals",
    label: "Requests and approvals",
    blurb:
      "Multi-step requests that require attributable authority and signed approvals, not unsigned consent.",
  },
  {
    id: "exception_handling",
    label: "Exception handling",
    blurb:
      "When standard flow breaks, who is allowed to deviate, on what authority, and with what receipt.",
  },
  {
    id: "assisted_journeys_handoffs",
    label: "Assisted journeys and handoffs",
    blurb:
      "Guided journeys where ownership moves between teams, organisations, or systems without losing accountability.",
  },
  {
    id: "ai_supported_decisions",
    label: "AI-supported decisions",
    blurb:
      "Where an AI contributes to a recommendation, decision, or action, and a human or organisation must remain accountable.",
  },
  {
    id: "evidence_ready_workflows",
    label: "Evidence-ready workflows",
    blurb:
      "Operational flow that must produce an audit-ready record by default, not as an afterthought.",
  },
];

export const GOVERNANCE_SCOPES = [
  {
    id: "purpose_bound_exchange",
    label: "Purpose-bound exchange",
    blurb:
      "What information moves, for what specific purpose, and the receipt that proves the purpose was honoured.",
  },
  {
    id: "attributable_authority",
    label: "Attributable authority",
    blurb:
      "Who was authorised to act, under what mandate, and how that authority can be re-checked.",
  },
  {
    id: "signed_receipts",
    label: "Signed receipts",
    blurb:
      "Cryptographically signed receipts for requests, approvals, actions, and handoffs that survive the workflow.",
  },
  {
    id: "selective_disclosure",
    label: "Selective disclosure",
    blurb:
      "Sharing only what is necessary, to whom it is necessary, with the rest remaining under user or operator custody.",
  },
];

const NEXT_STEP_DEFS = {
  public_demo: {
    id: "public_demo",
    label: "Walk through the public demo",
    summary:
      "A pre-prepared, synthetic demo showing how a governed workflow runs end-to-end — useful for an initial credibility check before any private engagement.",
    cta: "View public demo path",
  },
  private_sandbox: {
    id: "private_sandbox",
    label: "Request a private sandbox",
    summary:
      "A bounded, white-label environment seeded with synthetic data shaped to your sector and problem. No production data is required.",
    cta: "Request private sandbox",
  },
  pilot_scoping: {
    id: "pilot_scoping",
    label: "Open a pilot scoping conversation",
    summary:
      "A short, structured conversation to define a candidate pilot, the governance boundary, and what evidence the pilot would produce.",
    cta: "Request pilot scoping",
  },
};

// Sector × user-type recommendation matrix
const RECOMMENDATION_MATRIX = {
  buyer_sponsor: "pilot_scoping",
  operator_domain_lead: "private_sandbox",
  partner_integrator: "private_sandbox",
  governance_assurance_lead: "public_demo",
};

const CASE_STUDIES = {
  health: {
    title: "HealthCierge — predictive cardiovascular research, patient-held custody",
    body: "HealthCierge is a KATLAS case study for predictive cardiovascular research. Patients retain custody of their own records; purpose-bound consent governs what flows to a given study, for what window, and under what authority. Researchers run AI-assisted analysis near the data without centralising sensitive health records, and every contributing patient holds a signed receipt of what was used and for which approved purpose.",
  },
  supply_chain: {
    title: "FreightCierge — customs assurance and exception handling",
    body: "FreightCierge is a KATLAS case study for customs assurance and supply-chain exception handling. AI surfaces likely document mismatches or readiness issues as they emerge, shifting assurance from periodic review to near real-time triage. KATLAS governs the resulting requests, approvals, and handoffs between operator, carrier, and assurance body — producing an evidence pack of signed receipts that can be replayed later without centralising sensitive commercial data.",
  },
  mobility: {
    title: "PassengerCierge — governed passenger support across operators",
    body: "PassengerCierge is a KATLAS case study for governed passenger support across complex transport journeys. Operators, staff, and service partners coordinate requests, approvals, handoffs, and exceptions through purpose-bound exchange and signed receipts. Each handoff carries attributable authority; each operator sees only what it needs for its leg of the journey, and the passenger retains custody of the rest.",
  },
  cities_resilience: {
    // Note: IntelCierge here is a v1 cross-sector placeholder, not a settled
    // long-term taxonomy decision. IntelCierge is broader than Cities & Resilience.
    // See /app/memory/CONTEXT.md §7 for the canonical caveat.
    title: "IntelCierge — governed performance reporting across organisations",
    body: "IntelCierge is a KATLAS case study for governed performance reporting across complex organisations. Source-verifiable metrics, accountable handoffs, and trusted intelligence flow between agencies and operating units without centralising sensitive operational data. Authority to act in each jurisdiction is attributable to a named role; signed receipts capture what was decided, by whom, and at what time, producing an audit-ready record by default.",
  },
};

export function buildOutput(selection) {
  const { userTypeId, sectorId, problemId, scopeId } = selection;
  const userType = USER_TYPES.find((u) => u.id === userTypeId);
  const sector = SECTORS.find((s) => s.id === sectorId);
  const problem = PROBLEMS.find((p) => p.id === problemId);
  const scope = GOVERNANCE_SCOPES.find((g) => g.id === scopeId);
  const nextStepKey = RECOMMENDATION_MATRIX[userTypeId] || "public_demo";
  const nextStep = NEXT_STEP_DEFS[nextStepKey];
  const caseStudy = CASE_STUDIES[sectorId];

  const headline = `Governed ${problem?.label.toLowerCase()} in ${sector?.label.toLowerCase()}`;

  const explainer = [
    `For a ${userType?.label.toLowerCase()}, the value of bringing KATLAS into ${sector?.label.toLowerCase()} sits in moving from undocumented coordination to ${problem?.label.toLowerCase()} that produce attributable evidence by default.`,
    `In this scenario, the governance boundary you have selected is ${scope?.label.toLowerCase()}: ${scope?.blurb.toLowerCase()}`,
    `Cierge Labs frames this as a bounded design space. You are not modelling production data here; you are shaping the contract — who can act, on what authority, with what receipt, and what is shared selectively rather than centralised.`,
  ];

  return {
    headline,
    summary_chip: `${userType?.label} · ${sector?.label} · ${problem?.label}`,
    selection: {
      user_type: userType,
      sector,
      problem,
      scope,
    },
    explainer,
    case_study: caseStudy,
    next_step: nextStep,
  };
}

export const NEXT_STEPS = NEXT_STEP_DEFS;
